import React, { FC, KeyboardEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useCoarsePointer from '../../hooks/use-coarse-pointer';
import styles from './chat-view.module.css';
import SendIcon from '../../images/send-icon.svg';
import {
  MAX_MESSAGE_LENGTH,
  READ_TICKS_LABEL,
  ReadTicks,
  authorColor,
  messageContent,
  messageRows,
  messageTime,
  messageTimestamp,
  notReadYet,
  personName,
  readTicks,
  roleLabel,
} from '../../helpers/conversation-helper';

const STICK_TO_BOTTOM_PX = 80;
const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP_PX = 10;

type ChatViewProps = {
  // On a touch screen the selected message's action bar takes the header's place.
  header?: ReactNode;
  messages: ConversationMessage[];
  pending: PendingMessage[];
  participants: ConversationParticipant[];
  currentPersonUuid: string;
  hasOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  canWrite: boolean;
  notice: string | null;
  composerHint?: string | null;
  onSend: (text: string) => void;
  onRetry: (message: PendingMessage) => void;
};

const TicksIcon: FC<{ ticks: ReadTicks }> = ({ ticks }) => (
  <svg
    className={`${styles.ticks} ${ticks === 'readByAll' ? styles.ticksRead : ''}`}
    viewBox={ticks === 'sent' ? '0 0 12 10' : '0 0 17 10'}
    role='img'
    aria-label={READ_TICKS_LABEL[ticks]}
  >
    <polyline points='1,5 4.5,8.5 11,1.5' />
    {ticks !== 'sent' && <polyline points='7.5,7.5 8.5,8.5 15,1.5' />}
  </svg>
);

const MessageMenu: FC<{ text?: string; up: boolean; onInfo: () => void; onClose: () => void }> = ({ text, up, onInfo, onClose }) => {
  const menu = useRef<HTMLDivElement>(null);
  // Polling re-renders every few seconds with a new onClose; the listeners and the focus are set up once.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    menu.current?.querySelector('button')?.focus();
    const onPointerDown = (event: PointerEvent) => {
      if (!menu.current?.contains(event.target as Node)) close.current();
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const choose = (action: () => void) => () => {
    action();
    onClose();
  };

  return (
    <div ref={menu} className={`${styles.menu} ${up ? styles.menuUp : ''}`} role='menu' aria-label='Opções da mensagem'>
      <button type='button' role='menuitem' onClick={choose(onInfo)}>
        Dados da mensagem
      </button>
      {text && (
        <button type='button' role='menuitem' onClick={choose(() => navigator.clipboard?.writeText(text))}>
          Copiar
        </button>
      )}
    </div>
  );
};

const MessageBubble: FC<{
  message: ConversationMessage;
  first: boolean;
  ticks: ReadTicks | null;
  selected: boolean;
  touch: boolean;
  menuOpen: boolean;
  onMenu: (open: boolean) => void;
  onLongPress: () => void;
  onOpenInfo: () => void;
  onImageLoad: () => void;
}> = ({ message, first, ticks, selected, touch, menuOpen, onMenu, onLongPress, onOpenInfo, onImageLoad }) => {
  const content = messageContent(message);
  const bubble = useRef<HTMLDivElement>(null);
  const [menuUp, setMenuUp] = useState(false);
  const press = useRef<{ x: number; y: number; timer: number; fired: boolean }>(null);

  // The message list clips what overflows it, so a menu low in the list opens upwards.
  const openMenu = () => {
    const list = bubble.current?.closest('[role=log]');
    if (list) {
      const bubbleBox = bubble.current.getBoundingClientRect();
      const listBox = list.getBoundingClientRect();
      setMenuUp(bubbleBox.bottom > listBox.top + listBox.height / 2);
    }
    onMenu(true);
  };

  const cancelPress = () => {
    if (press.current) window.clearTimeout(press.current.timer);
  };

  useEffect(() => cancelPress, []);

  // Our own long press, not contextmenu: iPadOS never fires it on a touch, and Android fires it after selecting text.
  const touchHandlers = touch
    ? {
        onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
          if (event.button !== 0) return;
          cancelPress();
          const started = { x: event.clientX, y: event.clientY, fired: false, timer: 0 };
          started.timer = window.setTimeout(() => {
            started.fired = true;
            onLongPress();
          }, LONG_PRESS_MS);
          press.current = started;
        },
        onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
          const started = press.current;
          if (started && Math.hypot(event.clientX - started.x, event.clientY - started.y) > LONG_PRESS_SLOP_PX) cancelPress();
        },
        onPointerUp: cancelPress,
        onPointerCancel: cancelPress,
        // The finger lifting after a long press would otherwise follow an image's link.
        onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => {
          if (!press.current?.fired) return;
          event.preventDefault();
          event.stopPropagation();
          press.current = null;
        },
      }
    : {};

  return (
    <div
      ref={bubble}
      className={`${styles.message} ${first ? styles.firstMessage : ''} ${selected || menuOpen ? styles.selected : ''}`}
      aria-current={selected || undefined}
      onContextMenu={(event) => {
        event.preventDefault();
        if (!touch) openMenu();
      }}
      {...touchHandlers}
    >
      {!touch && (
        <button
          type='button'
          className={styles.menuButton}
          aria-label='Opções da mensagem'
          aria-haspopup='menu'
          aria-expanded={menuOpen}
          onClick={() => (menuOpen ? onMenu(false) : openMenu())}
        >
          <svg viewBox='0 0 12 8' aria-hidden='true'>
            <polyline points='1,1.5 6,6.5 11,1.5' />
          </svg>
        </button>
      )}
      {!touch && menuOpen && <MessageMenu text={content.deleted ? undefined : content.text} up={menuUp} onInfo={onOpenInfo} onClose={() => onMenu(false)} />}
      {content.image &&
        (content.image.url ? (
          <a href={content.image.url} target='_blank' rel='noopener noreferrer' title='Abrir imagem em tamanho original'>
            <img src={content.image.url} alt='Imagem enviada na conversa' className={styles.image} onLoad={onImageLoad} />
          </a>
        ) : (
          <p className={styles.placeholder}>Imagem indisponível</p>
        ))}
      {content.text && (
        <p className={content.deleted ? styles.placeholder : styles.messageText}>
          {content.text}
          <span className={styles.metaSpacer} aria-hidden='true' />
        </p>
      )}
      <span className={styles.meta} title={messageTimestamp(message.sentAt)}>
        {content.edited && <span>editada · </span>}
        <span>{messageTime(message.sentAt)}</span>
        {ticks && <TicksIcon ticks={ticks} />}
      </span>
    </div>
  );
};

const AuthorName: FC<{ person: ConversationPerson }> = ({ person }) => {
  const role = roleLabel(person);

  return (
    <p className={styles.author}>
      <span style={{ color: authorColor(person.uuid) }}>{personName(person)}</span>
      {role && <span className={styles.authorRole}> · {role}</span>}
    </p>
  );
};

const BackIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M20 11H7.8l5.6-5.6L12 4l-8 8 8 8 1.4-1.4L7.8 13H20z' />
  </svg>
);

const MessageInfo: FC<{
  message: ConversationMessage;
  participants: ConversationParticipant[];
  touch: boolean;
  onClose: () => void;
}> = ({ message, participants, touch, onClose }) => {
  const content = messageContent(message);
  const pendingReaders = notReadYet(message, participants);

  return (
    <aside className={`${styles.info} ${touch ? styles.infoScreen : ''}`} aria-label='Dados da mensagem'>
      {touch ? (
        <div className={styles.infoBar}>
          <button type='button' className={styles.barButton} onClick={onClose} aria-label='Voltar'>
            <BackIcon />
          </button>
          <h2>Dados da mensagem</h2>
        </div>
      ) : (
        <div className={styles.infoHeader}>
          <h2>dados da mensagem</h2>
          <button type='button' className={styles.closeButton} onClick={onClose} aria-label='Fechar'>
            ×
          </button>
        </div>
      )}
      <p className={styles.infoQuote}>{content.text || (content.image ? 'Imagem' : '')}</p>
      <p className={styles.infoSent}>
        {personName(message.author)}, {messageTimestamp(message.sentAt)}
      </p>
      <h3>Lida por</h3>
      {message.readBy.length === 0 ? (
        <p className={styles.infoEmpty}>Ninguém leu ainda.</p>
      ) : (
        <ul>
          {message.readBy.map((read) => (
            <li key={read.reader.uuid}>
              <span>{personName(read.reader)}</span>
              <span className={styles.infoTime}>{messageTimestamp(read.readAt)}</span>
            </li>
          ))}
        </ul>
      )}
      {pendingReaders.length > 0 && (
        <>
          <h3>Ainda não leu</h3>
          <ul>
            {pendingReaders.map((person) => (
              <li key={person.uuid}>{personName(person)}</li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
};

const SelectionBar: FC<{
  barRef: React.Ref<HTMLDivElement>;
  text?: string;
  onBack: () => void;
  onInfo: () => void;
}> = ({ barRef, text, onBack, onInfo }) => (
  <div ref={barRef} className={styles.selectionBar} role='toolbar' aria-label='Mensagem selecionada'>
    <button type='button' className={styles.barButton} onClick={onBack} aria-label='Voltar'>
      <BackIcon />
    </button>
    <span className={styles.spacer} />
    {text && (
      <button
        type='button'
        className={styles.barButton}
        aria-label='Copiar'
        onClick={() => {
          navigator.clipboard?.writeText(text);
          onBack();
        }}
      >
        <svg viewBox='0 0 24 24' aria-hidden='true'>
          <path d='M16 1H4a2 2 0 0 0-2 2v14h2V3h12zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11z' />
        </svg>
      </button>
    )}
    <button type='button' className={styles.barButton} aria-label='Dados da mensagem' onClick={onInfo}>
      <svg viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M11 7h2v2h-2zm0 4h2v6h-2zm1-9a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16z' />
      </svg>
    </button>
  </div>
);

const PendingBubble: FC<{ message: PendingMessage; onRetry: (message: PendingMessage) => void }> = ({ message, onRetry }) => (
  <div className={`${styles.message} ${styles.pending}`}>
    <p className={styles.messageText}>
      {message.text}
      <span className={styles.metaSpacer} aria-hidden='true' />
    </p>
    {message.status === 'failed' ? (
      <span className={`${styles.meta} ${styles.failed}`}>
        Não enviada.&nbsp;
        <button type='button' className={styles.linkButton} onClick={() => onRetry(message)}>
          tentar de novo
        </button>
      </span>
    ) : (
      <span className={styles.meta}>enviando…</span>
    )}
  </div>
);

const ChatView: FC<ChatViewProps> = (props) => {
  const [text, setText] = useState('');
  const [menuFor, setMenuFor] = useState<string>(null);
  const [infoFor, setInfoFor] = useState<string>(null);
  const [selectedFor, setSelectedFor] = useState<string>(null);
  const touch = useCoarsePointer();
  const selectionBar = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const textArea = useRef<HTMLTextAreaElement>(null);
  const stickToBottom = useRef(true);
  const firstMessageId = useRef<string>(null);
  const scrollHeightBefore = useRef(0);

  const scrollToBottom = () => {
    const element = container.current;
    if (element) element.scrollTop = element.scrollHeight;
  };

  // An older page goes on top: keep the messages the person was reading where they were. Anything else
  // follows the bottom only while the person is there, so polling never pulls them away from what they read.
  useLayoutEffect(() => {
    const element = container.current;
    if (!element) return;

    const first = props.messages[0]?.id || null;
    const prepended = firstMessageId.current !== null && first !== firstMessageId.current;
    if (prepended) {
      element.scrollTop += element.scrollHeight - scrollHeightBefore.current;
    } else if (stickToBottom.current) {
      scrollToBottom();
    }
    firstMessageId.current = first;
    scrollHeightBefore.current = element.scrollHeight;
  }, [props.messages, props.pending]);

  const onScroll = () => {
    const element = container.current;
    stickToBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < STICK_TO_BOTTOM_PX;
    scrollHeightBefore.current = element.scrollHeight;
  };

  // scrollHeight leaves out the border, which border-box counts: without it the box shows a scrollbar a line early.
  const fitTextArea = () => {
    const element = textArea.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight + element.offsetHeight - element.clientHeight}px`;
  };

  useLayoutEffect(fitTextArea, [text]);

  const send = () => {
    const message = text.trim();
    if (!message) return;

    stickToBottom.current = true;
    props.onSend(message);
    setText('');
  };

  // On a touch screen Enter breaks a line and only the button sends, as in WhatsApp on Android.
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!touch && event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  const selectedId = touch ? selectedFor : null;

  useEffect(() => {
    if (!selectedId) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!selectionBar.current?.contains(event.target as Node)) setSelectedFor(null);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedFor(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedId]);

  const onImageLoad = () => {
    if (stickToBottom.current) scrollToBottom();
  };

  const rows = messageRows(props.messages);
  const infoMessage = props.messages.find((message) => message.id === infoFor);
  const selectedMessage = props.messages.find((message) => message.id === selectedId);
  const selectedContent = selectedMessage && messageContent(selectedMessage);
  const sideClass = (author: ConversationPerson) => (author.uuid === props.currentPersonUuid ? styles.mine : styles.others);

  return (
    <div className={styles.screen}>
      <div className={styles.headerSlot}>
        {/* Hidden rather than unmounted, so the conversation below doesn't move while a message is selected. */}
        <div className={styles.headerContent} aria-hidden={selectedMessage ? true : undefined} inert={!!selectedMessage}>
          {props.header}
        </div>
        {selectedMessage && (
          <SelectionBar
            barRef={selectionBar}
            text={selectedContent.deleted ? undefined : selectedContent.text}
            onBack={() => setSelectedFor(null)}
            onInfo={() => {
              setInfoFor(selectedMessage.id);
              setSelectedFor(null);
            }}
          />
        )}
      </div>
      <div className={styles.rootContainer}>
        <div className={styles.chatContainer}>
          <div className={styles.body}>
            <div ref={container} className={styles.messageContainer} onScroll={onScroll} role='log' aria-label='Mensagens'>
              {props.hasOlder && (
                <button type='button' className={styles.olderButton} onClick={props.onLoadOlder} disabled={props.loadingOlder}>
                  {props.loadingOlder ? 'carregando…' : 'mensagens anteriores'}
                </button>
              )}
              {rows.map((row) =>
                row.kind === 'day' ? (
                  <p key={row.key} className={styles.day}>
                    <span>{row.label}</span>
                  </p>
                ) : (
                  <div key={row.key} className={`${styles.block} ${sideClass(row.author)}`}>
                    {row.author.uuid !== props.currentPersonUuid && <AuthorName person={row.author} />}
                    {row.messages.map((message, index) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        first={index === 0}
                        ticks={readTicks(message, props.participants)}
                        selected={message.id === infoFor || message.id === selectedId}
                        touch={touch}
                        menuOpen={message.id === menuFor}
                        onMenu={(open) => setMenuFor(open ? message.id : null)}
                        onLongPress={() => setSelectedFor(message.id)}
                        onOpenInfo={() => setInfoFor(message.id)}
                        onImageLoad={onImageLoad}
                      />
                    ))}
                  </div>
                )
              )}
              {props.pending.length > 0 && (
                <div className={`${styles.block} ${styles.mine}`}>
                  {props.pending.map((message) => (
                    <PendingBubble key={message.id} message={message} onRetry={props.onRetry} />
                  ))}
                </div>
              )}
            </div>
            {infoMessage && !touch && (
              <MessageInfo message={infoMessage} participants={props.participants} touch={false} onClose={() => setInfoFor(null)} />
            )}
          </div>
          {props.notice && <p className={styles.notice}>{props.notice}</p>}
          {props.canWrite && props.composerHint && <p className={styles.composerHint}>{props.composerHint}</p>}
          {props.canWrite && (
            <div className={styles.inputContainer}>
              <textarea
                ref={textArea}
                rows={1}
                value={text}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder='Mensagem'
                aria-label='Mensagem'
                onChange={(event) => setText(event.target.value)}
                onKeyDown={onKeyDown}
                enterKeyHint={touch ? 'enter' : 'send'}
                className={styles.inputField}
              />
              <button type='button' onClick={send} className={styles.sendButton} disabled={!text.trim()} aria-label='Enviar'>
                <img src={SendIcon} alt='' className={styles.sendButtonIcon} />
              </button>
            </div>
          )}
        </div>
      </div>
      {infoMessage && touch && (
        <MessageInfo message={infoMessage} participants={props.participants} touch={true} onClose={() => setInfoFor(null)} />
      )}
    </div>
  );
};

export default ChatView;
